// Global toast notifications.
//
// Feedback about an action the user just took is shown here rather than as a
// banner inside the page. A banner renders at the top of its form, so a user who
// pressed Save at the bottom of a long page never sees it; a toast is anchored
// to the viewport and is visible wherever they are scrolled to.
//
// This is for ACTION feedback only. Two things deliberately stay in the page:
//   - field-level validation, which has to sit next to the field it describes
//   - "could not load this page" states, where the toast would be the only
//     thing on screen
import {
  createContext, useContext, useState, useCallback, useRef, useEffect,
} from 'react';

const ToastContext = createContext(null);

const DEFAULT_DURATION = { error: 6000, success: 3500, info: 4500 };

const TONE_STYLES = {
  error: 'border-red-200 bg-red-50 text-red-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  info: 'border-slate-200 bg-white text-slate-800',
};

const TONE_ICON = { error: '✕', success: '✓', info: 'i' };

const TONE_ICON_STYLES = {
  error: 'bg-red-600 text-white',
  success: 'bg-emerald-600 text-white',
  info: 'bg-slate-700 text-white',
};

let nextId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timers = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts((list) => list.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const showToast = useCallback(
    (message, { tone = 'info', duration } = {}) => {
      const text = typeof message === 'string' ? message.trim() : '';
      if (!text) return null;
      const id = ++nextId;
      setToasts((list) => {
        // The same message firing twice (a double-click, a retry) should not
        // stack up duplicates.
        const deduped = list.filter((t) => !(t.message === text && t.tone === tone));
        // Cap the stack so a burst of failures cannot cover the page.
        return [...deduped, { id, message: text, tone }].slice(-4);
      });
      const ms = duration ?? DEFAULT_DURATION[tone] ?? DEFAULT_DURATION.info;
      timers.current.set(id, setTimeout(() => dismiss(id), ms));
      return id;
    },
    [dismiss]
  );

  const showError = useCallback(
    (message, opts) => showToast(message, { ...opts, tone: 'error' }),
    [showToast]
  );
  const showSuccess = useCallback(
    (message, opts) => showToast(message, { ...opts, tone: 'success' }),
    [showToast]
  );
  const showInfo = useCallback(
    (message, opts) => showToast(message, { ...opts, tone: 'info' }),
    [showToast]
  );

  // Clear pending timers if the provider ever unmounts.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach(clearTimeout);
      pending.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, showError, showSuccess, showInfo, dismiss }}>
      {children}
      <div
        // aria-live so a screen reader announces the toast without moving focus,
        // which would yank the user away from the control they just used.
        aria-live="polite"
        aria-atomic="false"
        className="pointer-events-none fixed right-4 top-4 z-[100] flex w-[calc(100%-2rem)] max-w-sm flex-col gap-2 sm:w-full"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.tone === 'error' ? 'alert' : 'status'}
            className={`pointer-events-auto flex items-start gap-3 rounded-lg border px-3 py-2.5 shadow-lg ${TONE_STYLES[t.tone] || TONE_STYLES.info}`}
          >
            <span
              aria-hidden
              className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold ${TONE_ICON_STYLES[t.tone] || TONE_ICON_STYLES.info}`}
            >
              {TONE_ICON[t.tone] || TONE_ICON.info}
            </span>
            <p className="flex-1 whitespace-pre-line text-sm leading-snug">{t.message}</p>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              className="-mr-1 shrink-0 rounded p-0.5 text-lg leading-none opacity-60 transition hover:opacity-100"
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within a ToastProvider');
  return ctx;
}

export default ToastContext;
